import { Button } from '@talentmesh/ui/components/button';
import { Slider } from '@talentmesh/ui/components/slider';
import { useEffect, useState } from 'react';
import { PossibleMatchScoreRanges } from '../../../api/useIdentifiedProfiles';
import { useIdentifiedListContext } from '../utils/IdentifiedListContext';
import BaseFilter from '@/Pages/Candidates/Overview/Components/Filter/BaseFilter';
import BaseFilterPanelContent from '@/Pages/Candidates/Overview/Components/Filter/BaseFilterPanelContent';
import UIStrings from '@/Utils/UIStrings';

type ValueChangeHandlerType = (value: PossibleMatchScoreRanges) => void;

export const defaultMatchScoreMin: number = 0;
export const defaultMatchScoreMax: number = 100;
export const defaultMatchScoreRange: PossibleMatchScoreRanges = [defaultMatchScoreMin, defaultMatchScoreMax];

const matchScoreRangesSuggestions: PossibleMatchScoreRanges[] = [
    [0, 24],
    [25, 49],
    [50, 74],
    [75, 100],
];

const formatRange = (value: PossibleMatchScoreRanges) => `${value[0]}-${value[1]}`;

const MatchScoreFilter = () => {
    const { options, setOptions } = useIdentifiedListContext();
    const currentValue =
        options.matchScoreMin === undefined || options.matchScoreMax === undefined
            ? defaultMatchScoreRange
            : [options.matchScoreMin, options.matchScoreMax];
    const [rangeValue, setRangeValue] = useState(currentValue);

    const resetCallback = () => {
        setOptions((p) => ({ ...p, matchScoreMin: defaultMatchScoreMin, matchScoreMax: defaultMatchScoreMax }));
        setRangeValue(defaultMatchScoreRange);
    };

    const handleValueChange: ValueChangeHandlerType = (value) => {
        if (value[0] < value[1]) {
            setRangeValue(value);
        }
    };

    const handleValueCommit: ValueChangeHandlerType = (value) => {
        if (value[0] < value[1]) {
            const flattenedMatchScoreFilters = value.flat();
            const matchScoreMin = flattenedMatchScoreFilters ? Math.min(...flattenedMatchScoreFilters) : undefined;
            const matchScoreMax = flattenedMatchScoreFilters ? Math.max(...flattenedMatchScoreFilters) : undefined;

            setRangeValue(value);
            setOptions((prev) => ({ ...prev, matchScoreMin, matchScoreMax }));
        }
    };

    useEffect(() => {
        setRangeValue(currentValue);
    }, [options.matchScoreMin, options.matchScoreMax]);

    return (
        <BaseFilter label={UIStrings.MatchScore} activeFilterCount={0}>
            <BaseFilterPanelContent resetCallback={resetCallback}>
                <div className="flex flex-col gap-y-3 p-4 pt-6">
                    <div className="flex flex-col">
                        <Slider
                            className="w-full"
                            min={defaultMatchScoreMin}
                            max={defaultMatchScoreMax}
                            step={1}
                            value={rangeValue}
                            onValueChange={handleValueChange}
                            onValueCommit={handleValueCommit}
                            data-cy="matchScoreRangeSlider"
                        />
                        <div className="mt-2 flex justify-between">
                            <span className="text-muted-foreground text-xs">{rangeValue[0]}</span>
                            <span className="text-muted-foreground text-xs">{rangeValue[1]}</span>
                        </div>
                    </div>
                    <div className="flex flex-row gap-2">
                        {matchScoreRangesSuggestions.map((value) => (
                            <Button
                                key={value.join()}
                                data-cy={`range-${value.join('-')}`}
                                onClick={() => handleValueCommit(value)}
                                size="xs"
                                variant="default-outline"
                            >
                                {formatRange(value)}
                            </Button>
                        ))}
                    </div>
                </div>
            </BaseFilterPanelContent>
        </BaseFilter>
    );
};

export default MatchScoreFilter;
